import { useState, useEffect, useCallback, useRef } from 'react';
import {
  useDatacenter,
  useDrafts,
  useTemplates,
  useDeleteDraft,
  useApplyTemplate,
  useExportDatacenter,
  Draft,
  Template
} from './useDataCenterApi';
import { useDataCenterDrafts } from './useDataCenterDrafts';
import { autoDraftId } from '../utils/editorDrafts.logic';

export function useDataCenterEditor(code: string, stageRef: React.RefObject<any>) {
  const dcQuery = useDatacenter(code);
  const draftsQuery = useDrafts(code);
  const templatesQuery = useTemplates(code);
  const deleteDraftMut = useDeleteDraft(code);
  const applyTemplateMut = useApplyTemplate(code);
  const exportMut = useExportDatacenter(code);

  const dc = dcQuery.data;
  const drafts: Draft[] = draftsQuery.data || [];
  const templates: Template[] = templatesQuery.data || [];
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(null);
  const hadDraftId = useRef(false);

  useEffect(() => {
    const next = autoDraftId(drafts, currentDraftId, hadDraftId.current);
    if (next !== currentDraftId) setCurrentDraftId(next);
    if (currentDraftId != null) hadDraftId.current = true;
  }, [drafts, currentDraftId]);

  const { handleSave, handlePublish } = useDataCenterDrafts(code, currentDraftId, setCurrentDraftId, dc, hadDraftId);

  const handleDeleteDraft = useCallback(async (id: number) => {
    await deleteDraftMut.mutateAsync(id);
    if (currentDraftId === id) setCurrentDraftId(null);
  }, [deleteDraftMut, currentDraftId]);

  const handleApplyTemplate = useCallback(async (templateCode: string) => {
    await applyTemplateMut.mutateAsync(templateCode);
  }, [applyTemplateMut]);

  const handleExportJson = useCallback(async () => {
    const data = await exportMut.mutateAsync();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dc?.code}-export.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [exportMut, dc]);

  const handleExportPng = useCallback(() => {
    if (!stageRef || !stageRef.current) return;
    const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${dc?.code || 'datacenter'}-layout.png`;
    a.click();
  }, [dc, stageRef]);

  return {
    dc,
    dcQuery,
    drafts,
    templates,
    currentDraftId,
    setCurrentDraftId,
    handleSave,
    handlePublish,
    handleDeleteDraft,
    handleApplyTemplate,
    handleExportJson,
    handleExportPng,
  };
}
